import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { moduleForPath } from '@/lib/permissions';
import {
  House,
  ChartLine,
  Receipt,
  Money,
  Bank,
  Calculator,
  FileText,
  Calendar,
  Robot,
  Gear,
  Users,
  ClipboardText,
  CreditCard,
  SignOut,
  Scales,
  Notebook,
  CurrencyInr,
  ArrowsClockwise,
  Buildings,
  IdentificationBadge,
  ShoppingCart,
  Megaphone,
  Package,
  Truck,
  ChartBar,
  Target,
  Cube,
  UserCircle,
  PlusCircle,
  ArrowsLeftRight,
  TrendUp,
  MapPin,
  Lightbulb,
  Sparkle,
  Percent,
  Table,
  ShieldCheck,
  UserGear,
} from '@phosphor-icons/react';

const menuItems = [
  { section: 'Overview', items: [
    { path: '/', icon: House, label: 'Dashboard' },
    { path: '/daily-tracker', icon: ChartLine, label: 'Daily Tracker' },
    { path: '/revenue-tracker', icon: ChartLine, label: 'Revenue Tracker' },
    { path: '/analytics', icon: Lightbulb, label: 'Analytics & Insights' },
    { path: '/clinic-trends', icon: TrendUp, label: 'Clinic Trends' },
    { path: '/ai-insights', icon: Sparkle, label: 'AI Insights' },
  ]},
  { section: 'Accounting', items: [
    { path: '/invoices', icon: Receipt, label: 'Invoices' },
    { path: '/create-invoice', icon: PlusCircle, label: 'Create Invoice' },
    { path: '/payments', icon: Money, label: 'Payments' },
    { path: '/banks', icon: Bank, label: 'Banks & Accounts' },
    { path: '/cash-ledger', icon: Money, label: 'Cash Ledger' },
    { path: '/journal-entries', icon: Notebook, label: 'Journal Entries' },
    { path: '/chart-of-accounts', icon: Scales, label: 'Chart of Accounts' },
  ]},
  { section: 'Sales', items: [
    { path: '/create-sales-order', icon: PlusCircle, label: 'Create Sales Order' },
    { path: '/lead-sources', icon: Target, label: 'Lead Sources' },
    { path: '/audiologist-master', icon: IdentificationBadge, label: 'Audiologist Master' },
    { path: '/customers', icon: UserCircle, label: 'Customers' },
    { path: '/customer-reports', icon: ChartLine, label: 'Enterprise Reports' },
    { path: '/discount-report', icon: Percent, label: 'Discount Report' },
    { path: '/item-wise-report', icon: Table, label: 'Item-wise Report' },
    { path: '/invoice-report', icon: Receipt, label: 'Invoice Report' },
    { path: '/sales-order-report', icon: ClipboardText, label: 'Sales Order Report' },
    { path: '/purchase-report', icon: ShoppingCart, label: 'Purchase Report' },
    { path: '/accounting-reports', icon: Notebook, label: 'Day Book & Ageing' },
    { path: '/daily-clinic-revenue', icon: ChartBar, label: 'Daily Clinic Revenue' },
  ]},
  { section: 'Inventory & Billing', items: [
    { path: '/items-master', icon: Cube, label: 'Items Master' },
    { path: '/inventory', icon: Package, label: 'Inventory & Billing' },
    { path: '/order-management', icon: Truck, label: 'Order Management' },
    { path: '/transfer-orders', icon: ArrowsLeftRight, label: 'Transfer Orders' },
    { path: '/billing-analytics', icon: ChartBar, label: 'Billing Analytics' },
    { path: '/margin-tracker', icon: TrendUp, label: 'Margin Tracker' },
  ]},
  { section: 'Expenses', items: [
    { path: '/vendor-management', icon: Buildings, label: 'Vendor Management' },
    { path: '/purchase-orders', icon: ClipboardText, label: 'Purchase Orders' },
    { path: '/vendor-bills', icon: FileText, label: 'Vendor Bills' },
    { path: '/vendor-credit', icon: CurrencyInr, label: 'Vendor Credit' },
  ]},
  { section: 'HR & Payroll', items: [
    { path: '/hr', icon: IdentificationBadge, label: 'HR & Payroll' },
    { path: '/employee-master', icon: Users, label: 'Employee Directory' },
    { path: '/clinics-master', icon: Buildings, label: 'Clinics Master' },
    { path: '/payroll', icon: Money, label: 'Payroll Management' },
    { path: '/announcements', icon: Megaphone, label: 'Announcements & Gallery' },
  ]},
  { section: 'Reconciliation', items: [
    { path: '/bank-reconciliation', icon: Bank, label: 'Bank Reconciliation' },
  ]},
  { section: 'Compliance', items: [
    { path: '/gst', icon: Calculator, label: 'GST' },
    { path: '/tds', icon: CurrencyInr, label: 'TDS' },
    { path: '/e-invoicing', icon: ShieldCheck, label: 'e-Invoicing' },
    { path: '/compliance-calendar', icon: Calendar, label: 'Compliance Calendar' },
  ]},
  { section: 'Reports', items: [
    { path: '/financial-statements', icon: FileText, label: 'Financial Statements' },
  ]},
  { section: 'Intelligence', items: [
    { path: '/ai-alerts', icon: Robot, label: 'AI Alerts' },
  ]},
  { section: 'Admin', items: [
    { path: '/payment-modes', icon: CreditCard, label: 'Payment Modes', adminOnly: true },
    { path: '/zoho-sync', icon: ArrowsClockwise, label: 'Legacy Data (Zoho)', adminOnly: true },
    { path: '/users', icon: Users, label: 'Users', adminOnly: true },
    { path: '/roles', icon: ShieldCheck, label: 'Roles & Permissions', adminOnly: true },
    { path: '/team-access', icon: UserGear, label: 'Team Access', adminOnly: true },
    { path: '/audit-logs', icon: ClipboardText, label: 'Audit Logs' },
    { path: '/settings', icon: Gear, label: 'Settings' },
  ]},
];

export default function Sidebar() {
  const { user, logout, isAdmin, can } = useAuth();
  const location = useLocation();

  const isVisible = (item) => {
    if (item.adminOnly && !isAdmin()) return false;
    return can(moduleForPath(item.path), 'view');
  };

  return (
    <aside className="sidebar" data-testid="sidebar">
      <div className="sidebar-logo">
        <div className="bg-white rounded-lg p-1.5 flex items-center shadow-sm">
          <img
            src="/fintexa-icon.png"
            alt="Fintexa"
            className="h-6 w-auto"
            data-testid="sidebar-logo-icon"
          />
        </div>
        <span className="ml-2.5 text-lg font-semibold font-['Plus_Jakarta_Sans'] text-white">Fintexa</span>
      </div>
      
      <nav className="sidebar-nav" data-testid="sidebar-nav">
        {menuItems.map((section) => {
          const visibleItems = section.items.filter(isVisible);
          if (visibleItems.length === 0) return null;
          return (
          <div key={section.section}>
            <div className="sidebar-section-title">{section.section}</div>
            {visibleItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`sidebar-item ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </div>
          );
        })}
      </nav>
      
      <div className="p-4 border-t border-[rgba(234,231,225,0.1)]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-[#0B3C68] flex items-center justify-center">
            <span className="text-sm font-medium">{user?.full_name?.[0] || 'U'}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.full_name || 'User'}</p>
            <p className="text-xs opacity-60 capitalize">{user?.role || 'viewer'}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-[rgba(255,255,255,0.1)] transition-colors"
          data-testid="logout-btn"
        >
          <SignOut size={18} />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}
